import { isValidEmail } from "../services/validations";
import googleAnalytics from "../services/google-analytics";
import api from "../services/api";

class JoinForm {
  form: HTMLFormElement;

  emailInput: HTMLInputElement;
  submitButton: HTMLButtonElement;

  submitButtonText: HTMLElement | null;

  emailRequiredError: HTMLElement | null;
  emailInvalidError: HTMLElement | null;
  emailRegisteredError: HTMLElement | null;

  additionalJoinParams: Record<string, object>;

  processing = false;

  constructor(form: HTMLFormElement) {
    this.form = form;

    this.emailInput = this.form.querySelector("[name=email]") as HTMLInputElement;
    this.submitButton = this.form.querySelector("button[type='submit']") as HTMLButtonElement;

    this.submitButtonText = this.submitButton?.querySelector("span");

    this.emailRequiredError = this.form.querySelector("[email-required-error]");
    this.emailInvalidError = this.form.querySelector("[email-invalid-error]");
    this.emailRegisteredError = this.form.querySelector("[email-registered-error]");

    const additionalParams = this.form.getAttribute("additional-join-params");

    if (additionalParams) {
      this.additionalJoinParams = JSON.parse(additionalParams);
    } else {
      this.additionalJoinParams = {};
    }

    if (!this.emailInput && !this.submitButton) {
      console.error("JoinForm: missing required elements");
      return;
    }

    this.clearErrors();
    this.setProcessingState(false);

    this.attachListeners();
  }

  handleSubmit() {
    if (this.processing) {
      return;
    }

    this.clearErrors();
    this.setProcessingState(false);

    const email = this.emailInput.value.trim();

    if (email == "") {
      this.show(this.emailRequiredError);
      return;
    }

    if (!isValidEmail(email)) {
      this.show(this.emailInvalidError);
      return;
    }

    const urlSearchParams = new window.URLSearchParams(window.location.search);
    const queryParams = Object.fromEntries(urlSearchParams.entries());

    const userParams = Object.assign({}, queryParams, this.additionalJoinParams, { email });

    this.setProcessingState(true);

    api
      .register(userParams)
      .then((response) => response.json())
      .then((data) => {
        this.logEvent();

        if (data["redirect_to"]) {
          window.location.href = data["redirect_to"];
        } else {
          window.location.reload();
        }
      })
      .catch((response) => {
        this.isAlreadyRegisteredError(response)
          .then(() => {
            this.show(this.emailRegisteredError);
          })
          .catch(() => {
            console.warn("Error registering user", response);
          })
          .finally(() => {
            this.setProcessingState(false);
          });
      });
  }

  clearErrors() {
    this.hide(this.emailRequiredError);
    this.hide(this.emailInvalidError);
    this.hide(this.emailRegisteredError);
  }

  attachListeners() {
    this.form.addEventListener("submit", (event) => {
      event.preventDefault();

      this.handleSubmit();
    });

    this.emailInput.addEventListener("input", () => {
      this.clearErrors();
    });
  }

  setProcessingState(processing: boolean) {
    this.submitButton.disabled = processing;
    this.emailInput.disabled = processing;

    if (processing) {
      this.hide(this.submitButtonText);
      this.show(this.submitButton?.querySelector("[processing-icon]"));
    } else {
      this.hide(this.submitButton?.querySelector("[processing-icon]"));
      this.show(this.submitButtonText);
    }

    this.processing = processing;
  }

  hide(element: HTMLElement | null) {
    element?.classList.add("hidden");
  }

  show(element: HTMLElement | null) {
    element?.classList.remove("hidden");
  }

  isAlreadyRegisteredError(response: Response): Promise<void> {
    return response
      .json()
      .then((data) => {
        if (data?.errors?.email?.indexOf("has already been taken") !== -1) {
          return Promise.resolve();
        }

        return Promise.reject();
      })
      .catch((error) => {
        console.log("ERROR", error);
        return Promise.reject();
      });
  }

  logEvent() {
    googleAnalytics.pushEvent({
      event: "SIGNUP",
      eventCategory: "SIGNUP",
      eventAction: "email",
    });
  }
}

export function initJoinForms() {
  document.querySelectorAll("form[join-form-v2]").forEach((form) => {
    new JoinForm(form as HTMLFormElement);
  });
}

document.addEventListener("DOMContentLoaded", initJoinForms);
